"use client";

import { cn } from "@/lib/utils";
import { IconArrowLeft, IconArrowRight } from "@tabler/icons-react";
import { EmblaCarouselType } from "embla-carousel";
import Autoplay, { AutoplayOptionsType } from "embla-carousel-autoplay";
import Fade from "embla-carousel-fade";
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: "horizontal" | "vertical";
  setApi?: (api: CarouselApi) => void;
  autoplay?: AutoplayOptionsType;
  fade?: boolean;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  scrollTo: (index: number) => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

function Carousel({
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  className,
  children,
  autoplay,
  fade,
  ...props
}: React.ComponentProps<"div"> & CarouselProps) {
  const pluginsWithAutoplay = React.useMemo(() => {
    const basePlugins = plugins
      ? Array.isArray(plugins)
        ? plugins
        : [plugins]
      : [];

    const autoplayPlugin = autoplay
      ? Autoplay({
          delay: 4000,
          stopOnInteraction: true,
          ...autoplay,
        })
      : null;

    const fadePlugin = fade ? Fade() : null;

    if (autoplayPlugin) basePlugins.push(autoplayPlugin);
    if (fadePlugin) basePlugins.push(fadePlugin);

    return basePlugins;
  }, [plugins, autoplay, fade]);

  const [carouselRef, api] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      axis: orientation === "horizontal" ? "x" : "y",
      ...opts,
    },
    pluginsWithAutoplay,
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return;
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const autoplayTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleInteraction = React.useCallback(() => {
    if (!api || !autoplay) return;
    const autoplayPlugin = api.plugins()?.autoplay;
    if (!autoplayPlugin) return;

    autoplayPlugin.stop();

    if (autoplayTimerRef.current) {
      clearTimeout(autoplayTimerRef.current);
    }

    autoplayTimerRef.current = setTimeout(() => {
      autoplayPlugin.play();
    }, 20000);
  }, [api, autoplay]);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
    handleInteraction();
  }, [api, handleInteraction]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
    handleInteraction();
  }, [api, handleInteraction]);

  const scrollTo = React.useCallback(
    (index: number) => {
      api?.scrollTo(index);
      handleInteraction();
    },
    [api, handleInteraction],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  React.useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on("reInit", onSelect);
    api.on("select", onSelect);

    return () => {
      api?.off("select", onSelect);
    };
  }, [api, onSelect]);

  React.useEffect(() => {
    if (!api || !autoplay) return;

    // Listen to pointer/touch events for manual interaction
    api.on("pointerDown", handleInteraction);
    api.on("pointerUp", handleInteraction);

    return () => {
      if (autoplayTimerRef.current) clearTimeout(autoplayTimerRef.current);
      api.off("pointerDown", handleInteraction);
      api.off("pointerUp", handleInteraction);
    };
  }, [api, autoplay, handleInteraction]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api,
        opts,
        orientation:
          orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        scrollTo,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div
        onKeyDownCapture={handleKeyDown}
        className={cn("relative", className)}
        role="region"
        aria-roledescription="carousel"
        data-slot="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

function CarouselContent({ className, ...props }: React.ComponentProps<"div">) {
  const { carouselRef } = useCarousel();

  return (
    <div
      ref={carouselRef}
      className="overflow-hidden"
      data-slot="carousel-content"
    >
      <div className={cn("flex", className)} {...props} />
    </div>
  );
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
  const { orientation } = useCarousel();

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className,
      )}
      {...props}
    />
  );
}

function CarouselButton({
  className,
  ...props
}: React.ComponentProps<"button">) {
  return (
    <button
      className={cn(
        "flex cursor-pointer items-center justify-center [&:disabled>div]:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

function CarouselPrevious({
  className,
  custom = false,
  ...props
}: React.ComponentProps<typeof CarouselButton> & { custom?: boolean }) {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();

  return (
    <CarouselButton
      data-slot="carousel-previous"
      className={cn(
        "absolute",
        {
          "top-1/2 -left-12 -translate-y-1/2":
            !custom && orientation === "horizontal",
          "-top-12 left-1/2 -translate-x-1/2 rotate-90":
            !custom && orientation !== "horizontal",
        },
        className,
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <div className="flex size-12 items-center justify-center rounded-full bg-white text-zinc-600 shadow-[0_0.125rem_0.75rem_rgba(0,0,0,0.1)] transition-all hover:bg-zinc-100 hover:text-primary-600 hover:shadow-[0_0.25rem_1rem_rgba(174,82,231,0.15)] dark:bg-zinc-800 dark:text-white dark:shadow-none dark:hover:bg-zinc-700 dark:hover:text-primary-300">
        <IconArrowLeft />
      </div>
      <span className="sr-only">Previous slide</span>
    </CarouselButton>
  );
}

function CarouselNext({
  className,
  custom = false,
  ...props
}: React.ComponentProps<typeof CarouselButton> & { custom?: boolean }) {
  const { orientation, scrollNext, canScrollNext } = useCarousel();

  return (
    <CarouselButton
      data-slot="carousel-next"
      className={cn(
        "absolute",
        {
          "top-1/2 -right-12 -translate-y-1/2":
            !custom && orientation === "horizontal",
          "-bottom-12 left-1/2 -translate-x-1/2 rotate-90":
            !custom && orientation !== "horizontal",
        },
        className,
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <div className="flex size-12 items-center justify-center rounded-full bg-white text-zinc-600 shadow-[0_0.125rem_0.75rem_rgba(0,0,0,0.1)] transition-all hover:bg-zinc-100 hover:text-primary-600 hover:shadow-[0_0.25rem_1rem_rgba(174,82,231,0.15)] dark:bg-zinc-800 dark:text-white dark:shadow-none dark:hover:bg-zinc-700 dark:hover:text-white">
        <IconArrowRight />
      </div>
      <span className="sr-only">Next slide</span>
    </CarouselButton>
  );
}

const useDotButton = () => {
  const { api: emblaApi } = useCarousel();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

export function CarouselDots({
  className,
  selectedClassName =
    "bg-zinc-700 dark:bg-zinc-300",
  ...props
}: React.ComponentProps<"div"> & {
  selectedClassName?: string;
}) {
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton();

  return (
    <div className={cn("flex justify-center gap-2", className)} {...props}>
      {scrollSnaps.map((_, index) => (
        <button
          key={index}
          onClick={() => onDotButtonClick(index)}
          className={cn(
            "h-2 w-2 cursor-pointer rounded-full bg-zinc-400 dark:bg-zinc-600 transition-colors hover:bg-zinc-600 dark:hover:bg-zinc-500",
            selectedIndex === index && selectedClassName,
          )}
          aria-label={`Go to slide ${index + 1}`}
        />
      ))}
    </div>
  );
}

export {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
};
